import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';

import { SelectOptionValue } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { Period } from 'dashboard/lib/unixTime';
import {
  AnalyticsQueryTemplate,
  computeOffsetForQueryTemplateLoad,
  InquiryAnalyticsQueryParams,
} from 'dashboard/models/analytics-query-template';
import {
  MetricsInquiryRecentActivityVolume,
  MetricsInquiryVolume,
  MetricsInquiryVolumeOverTime,
  MetricsTemplateFunnel,
  MetricsTemplateFunnelConversionOverTime,
  MetricsTemplateFunnelFlow,
  MetricsVerificationCheckSummary,
  MetricsVolumeSummary,
} from 'dashboard/models/metrics';
import {
  MetricsActions,
  MetricsActionType,
  MetricsFilterParams,
} from 'dashboard/redux/actions/metrics-inquiry';
import { FilterDimensions } from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/InquiryMetrics';
import { CountryCode } from 'lib/constants/locales';
import { Iso8601DateString } from 'lib/types';

export enum DynamicFilterOptionKey {
  CountryCodes = 'countryCodes',
  DeviceGroups = 'deviceGroups',
  Platforms = 'platforms',
}

export type DynamicFilterSet = Record<FilterDimensions, SelectOptionValue<string>[]>;

export type InquiryGranularities = {
  funnel: Period;
  overview: Period;
};

export interface DynamicFilterOptions {
  [DynamicFilterOptionKey.CountryCodes]: string[];
  [DynamicFilterOptionKey.DeviceGroups]: string[];
  [DynamicFilterOptionKey.Platforms]: string[];
}

export interface InquiriesQueriedState {
  volumeSummary: Iso8601DateString | undefined;
  funnel: Iso8601DateString | undefined;
  checks: Iso8601DateString | undefined;
  funnelFlow: Iso8601DateString | undefined;
  volume: Iso8601DateString | undefined;
  volumeOverTime: Iso8601DateString | undefined;
}

export interface MetricsState {
  volumeSummary: MetricsVolumeSummary;
  // v1 funnel analytics
  templateVolumeSummary: Record<string, MetricsVolumeSummary>;
  templateFunnels: Record<string, MetricsTemplateFunnel>;
  // v2 funnel analytics
  templateFunnelFlows: MetricsTemplateFunnelFlow[];
  templateFunnelConversionOverTime: MetricsTemplateFunnelConversionOverTime;
  // end v2 funnel analytics
  verificationCheckSummary: MetricsVerificationCheckSummary;
  filters: MetricsFilterParams;
  lastQueryFilters: {
    overview: MetricsFilterParams;
    funnel: MetricsFilterParams;
  };
  // In v2 of funnel analytics, we will show one template funnel at a time, regardless if it is dynamic flow
  // or inquiry 2.0.
  loadedTemplateOrInquiryTemplateId?: string;
  loadedInquiryTemplateVersionId?: string;
  volume?: MetricsInquiryVolume;
  volumeDelta: MetricsInquiryVolume | undefined;
  volumeOverTime: MetricsInquiryVolumeOverTime;
  inquiryTagIds: string[];
  recentActivityVolume: MetricsInquiryRecentActivityVolume;
  recentActivityVolumeDelta: MetricsInquiryRecentActivityVolume | undefined;
  /**
   * queriedAt is a separate state object because some of our other data are
   * stored as objects, which makes it hard to colocate. We also split out
   * queried at by UI tab.
   */
  queriedAt: InquiriesQueriedState;
  updatedAt: {
    funnelFlow: Iso8601DateString | undefined;
  };
  granularities: InquiryGranularities;
  medianCompletion: number;
  medianCompletionDelta: number | undefined;
  filterOptions: DynamicFilterOptions;
  topLevelFiltersChanged: boolean;
  selectedQueryTemplate: {
    template: AnalyticsQueryTemplate | undefined;
    computedOffsetStart: Iso8601DateString | undefined;
    computedOffsetEnd: Iso8601DateString | undefined;
  };
}

const NOW = DateTime.local();
const DEFAULT_CREATED_AT_START = NOW.minus({ days: 14 }).startOf('day');
const DEFAULT_CREATED_AT_END = NOW.endOf('day');
export const EMPTY_VOLUME_SUMMARY: MetricsVolumeSummary = {
  total: 0,

  started: 0,
  'started-rate': 0,
  pending: 0,
  'pending-rate': 0,
  'pending-and-expired': 0,
  'pending-and-expired-rate': 0,

  completed: 0,
  'completed-rate': 0,
  failed: 0,
  'failed-rate': 0,
  expired: 0,
  'expired-rate': 0,

  'completed-and-needs-review': 0,
  'completed-and-needs-review-rate': 0,
  'completed-and-approved': 0,
  'completed-and-approved-rate': 0,
  'completed-and-declined': 0,
  'completed-and-declined-rate': 0,

  'needs-review': 0,
  'needs-review-rate': 0,
  approved: 0,
  'approved-rate': 0,
  declined: 0,
  'declined-rate': 0,
};

export const initialInquiryMetricsFilterState: MetricsFilterParams = {
  createdAtStart: undefined,
  createdAtEnd: undefined,
  inquiryStatus: [],
  templateIds: [],
  groupBy: undefined,
  templateOrInquiryTemplateId: undefined,
  templateOrInquiryTemplateIds: [],
  funnelStepIds: undefined,
  inquiryTagIds: [],
  [DynamicFilterOptionKey.CountryCodes]: [],
  [DynamicFilterOptionKey.DeviceGroups]: [],
  [DynamicFilterOptionKey.Platforms]: [],
};

export const initialState: MetricsState = {
  volumeSummary: EMPTY_VOLUME_SUMMARY,
  templateVolumeSummary: {},
  templateFunnels: {},
  verificationCheckSummary: {
    data: [],
  },
  filters: initialInquiryMetricsFilterState,
  lastQueryFilters: {
    funnel: initialInquiryMetricsFilterState,
    overview: initialInquiryMetricsFilterState,
  },
  queriedAt: {
    volumeSummary: undefined,
    funnel: undefined,
    checks: undefined,
    funnelFlow: undefined,
    volume: undefined,
    volumeOverTime: undefined,
  },
  updatedAt: {
    funnelFlow: undefined,
  },
  templateFunnelConversionOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  templateFunnelFlows: [],
  inquiryTagIds: [],
  volume: undefined,
  volumeDelta: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  medianCompletion: undefined,
  medianCompletionDelta: undefined,
  volumeOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  filterOptions: {
    [DynamicFilterOptionKey.CountryCodes]: [],
    [DynamicFilterOptionKey.DeviceGroups]: [],
    [DynamicFilterOptionKey.Platforms]: [],
  },
  granularities: {
    funnel: Period.Day,
    overview: Period.Day,
  },
  // initial state is true to ensure we always load dynamic filters on first load
  topLevelFiltersChanged: true,
  selectedQueryTemplate: {
    template: undefined,
    computedOffsetStart: undefined,
    computedOffsetEnd: undefined,
  },
};

// This is a function so we always get the most up-to-date created at times
const getInitialState = (): MetricsState => ({
  ...initialState,
  filters: {
    ...initialState.filters,
  },
});

export default (state: MetricsState = initialState, action: MetricsActions): MetricsState => {
  switch (action.type) {
    case MetricsActionType.SetMetricsVolumeSummary: {
      return {
        ...state,
        volumeSummary: action.payload.volumeSummary,
        queriedAt: {
          ...state.queriedAt,
          volumeSummary: action.payload.queriedAt,
        },
        lastQueryFilters: {
          ...state.lastQueryFilters,
          overview: state.filters,
        },
      };
    }

    case MetricsActionType.SetMetricsInquiryRecentActivitySummary: {
      const { inquiryVolume, volumeDelta } = action.payload;
      return {
        ...state,
        recentActivityVolume: inquiryVolume,
        recentActivityVolumeDelta: volumeDelta,
      };
    }

    case MetricsActionType.SetMetricsInquiryVolume: {
      const { inquiryVolume, volumeDelta, queriedAt } = action.payload;
      return {
        ...state,
        volume: inquiryVolume,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        volumeDelta: volumeDelta,
        queriedAt: {
          ...state.queriedAt,
          volume: queriedAt,
        },
        lastQueryFilters: {
          ...state.lastQueryFilters,
          overview: state.filters,
        },
      };
    }

    case MetricsActionType.SetMetricsInquiryMedianCompletionVolume: {
      const { queriedAt, completionSummary, completionDelta } = action.payload;
      return {
        ...state,
        queriedAt: {
          ...state.queriedAt,
          volume: queriedAt,
        },
        medianCompletion: completionSummary?.data?.pct_50th,
        medianCompletionDelta: completionDelta?.data?.pct_50th,
      };
    }

    case MetricsActionType.SetMetricsInquiryVolumeOverTime: {
      return {
        ...state,
        volumeOverTime: action.payload.inquiryVolumeOverTime,
        queriedAt: {
          ...state.queriedAt,
          volumeOverTime: action.payload.queriedAt,
        },
        lastQueryFilters: {
          ...state.lastQueryFilters,
          overview: state.filters,
        },
      };
    }

    case MetricsActionType.SetMetricsTemplateVolumeSummary: {
      return {
        ...state,
        templateVolumeSummary: {
          ...state.templateVolumeSummary,
          [action.payload.id]: action.payload.volumeSummary,
        },
        lastQueryFilters: {
          ...state.lastQueryFilters,
          overview: state.filters,
        },
        queriedAt: {
          ...state.queriedAt,
          funnel: action.payload.queriedAt,
        },
      };
    }
    case MetricsActionType.SetMetricsTemplateFunnel: {
      return {
        ...state,
        templateFunnels: {
          ...state.templateFunnels,
          [action.payload.id]: action.payload.templateFunnel,
        },
        queriedAt: {
          ...state.queriedAt,
          funnel: action.payload.queriedAt,
        },
        filters: {
          ...state.filters,
          templateOrInquiryTemplateId: action.payload.id,
        },
      };
    }

    case MetricsActionType.SetMetricsVerificationCheckSummary: {
      return {
        ...state,
        verificationCheckSummary: action.payload.verificationCheckSummary,
        queriedAt: {
          ...state.queriedAt,
          checks: action.payload.queriedAt,
        },
      };
    }

    case MetricsActionType.ClearMetrics: {
      return { ...getInitialState(), filters: state.filters };
    }

    case MetricsActionType.ResetFiltersToInitialState: {
      return {
        ...state,
        filters: getInitialState().filters,
        selectedQueryTemplate: getInitialState().selectedQueryTemplate,
      };
    }

    case MetricsActionType.SetMetricsFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return {
          ...state,
          topLevelFiltersChanged: false,
        };
      }

      return {
        ...state,
        filters: newFilters,
        topLevelFiltersChanged: haveTopLevelFiltersChanged(state.filters, newFilters),
      };
    }

    case MetricsActionType.ResetMetricsDateFilter: {
      return {
        ...state,
        filters: {
          ...state.filters,
          createdAtStart: undefined,
          createdAtEnd: undefined,
        },
      };
    }

    case MetricsActionType.SetMetricsTemplateFunnelFlow: {
      return {
        ...state,
        templateFunnelFlows: action.payload.funnelFlows,
        queriedAt: {
          ...state.queriedAt,
          funnelFlow: action.payload.queriedAt,
        },
        filters: {
          ...state.filters,
        },
        loadedTemplateOrInquiryTemplateId: action.payload.inquiryTemplateId,
        updatedAt: {
          ...state.updatedAt,
          funnelFlow: action.payload.updatedAt,
        },
      };
    }

    case MetricsActionType.SetMetricsTemplateFunnelConversionOverTime: {
      return {
        ...state,
        lastQueryFilters: {
          ...state.lastQueryFilters,
          funnel: state.filters,
        },
        templateFunnelConversionOverTime: action.payload.funnelConversionOverTime,
      };
    }

    case MetricsActionType.SetMetricsTemplateIdLoaded: {
      return {
        ...state,
        loadedTemplateOrInquiryTemplateId: action.payload.templateId,
      };
    }

    case MetricsActionType.SetMetricsGranularities: {
      return {
        ...state,
        granularities: action.payload.granularities,
      };
    }

    case MetricsActionType.SetMetricsInquiryTemplateVersionIdLoaded: {
      return {
        ...state,
        loadedInquiryTemplateVersionId: action.payload.inquiryTemplateVersionId,
      };
    }

    case MetricsActionType.LoadQueryTemplate: {
      const { query, 'relative-time-span': relativeTimeSpan } =
        action.payload.queryTemplate.attributes;

      const queryParams = query as InquiryAnalyticsQueryParams;
      const { createdAtStart, createdAtEnd } = computeOffsetForQueryTemplateLoad(
        relativeTimeSpan,
        DateTime.local().endOf('day'),
      );

      return {
        ...state,
        selectedQueryTemplate: {
          template: action.payload.queryTemplate,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          computedOffsetStart: createdAtStart.toISO(),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          computedOffsetEnd: createdAtEnd.toISO(),
        },
        filters: {
          ...state.filters,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          createdAtStart: createdAtStart.toISO(),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          createdAtEnd: createdAtEnd.toISO(),
          templateOrInquiryTemplateIds: [
            ...queryParams['template-ids'],
            ...queryParams['inquiry-template-ids'],
          ],
          inquiryTagIds: [...queryParams['tag-ids']],
          [DynamicFilterOptionKey.CountryCodes]: queryParams['country-codes'] as CountryCode[],
          [DynamicFilterOptionKey.DeviceGroups]: queryParams['device-groups'],
          [DynamicFilterOptionKey.Platforms]: queryParams['platforms'],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          groupBy: queryParams['group-by'],
        },
      };
    }

    case MetricsActionType.SetMetricsInquiryFilterOptions: {
      // new dynamic filters for query result
      const {
        filterOptions: {
          country_codes: newCountryCodes,
          device_groups: newDeviceGroups,
          platforms: newPlatforms,
        },
      } = action.payload;

      return {
        ...state,
        filterOptions: {
          [DynamicFilterOptionKey.CountryCodes]: [...newCountryCodes],
          [DynamicFilterOptionKey.DeviceGroups]: [...newDeviceGroups],
          [DynamicFilterOptionKey.Platforms]: [...newPlatforms],
        },
      };
    }

    case MetricsActionType.UnsetSelectedTemplate: {
      return {
        ...state,
        selectedQueryTemplate: getInitialState().selectedQueryTemplate,
      };
    }

    default:
      return state;
  }
};

const haveTopLevelFiltersChanged = (
  lastFilters: MetricsFilterParams,
  currentFilters: MetricsFilterParams,
) => {
  // handle initial state
  if (lastFilters == null) {
    return false;
  }

  return (
    !isEqual(lastFilters.createdAtStart, currentFilters.createdAtStart) ||
    !isEqual(lastFilters.createdAtEnd, currentFilters.createdAtEnd) ||
    !isEqual(
      lastFilters.templateOrInquiryTemplateIds,
      currentFilters.templateOrInquiryTemplateIds,
    ) ||
    !isEqual(lastFilters.groupBy, currentFilters.groupBy)
  );
};
